import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMetronome } from '../contexts/MetronomeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const { FiPlay, FiPause, FiMinus, FiPlus, FiChevronDown, FiChevronUp } = FiIcons;
const { MdTouchApp } = MdIcons;

const TempoControl = () => {
  const { 
    tempo, 
    setTempo, 
    isPlaying, 
    togglePlay, 
    handleTapTempo,
    volumeSettings,
    setVolumeSettings
  } = useMetronome();
  const { t } = useLanguage();
  const [showAllPresets, setShowAllPresets] = useState(false);
  const [activeCategory, setActiveCategory] = useState('basic');

  // Basic tempo presets - optimized for mobile
  const basicTempoPresets = [
    { label: t('tempoPresets.slow'), value: 70 },
    { label: t('tempoPresets.andante'), value: 85 },
    { label: t('tempoPresets.moderate'), value: 100 },
    { label: t('tempoPresets.allegro'), value: 120 },
  ];

  // Extended tempo presets with musical terms
  const classicalTempoPresets = [
    { label: t('tempoPresets.larghissimo'), value: 30 },
    { label: t('tempoPresets.grave'), value: 40 },
    { label: t('tempoPresets.lento'), value: 50 },
    { label: t('tempoPresets.largo'), value: 55 },
    { label: t('tempoPresets.larghetto'), value: 60 },
    { label: t('tempoPresets.adagio'), value: 65 },
    { label: t('tempoPresets.allegretto'), value: 110 },
    { label: t('tempoPresets.vivace'), value: 140 },
    { label: t('tempoPresets.presto'), value: 170 },
    { label: t('tempoPresets.prestissimo'), value: 200 },
  ];

  const handleTempoChange = (newTempo) => {
    setTempo(Math.max(30, Math.min(240, newTempo)));
  };

  const handleVolumeChange = (value) => {
    setVolumeSettings(prev => ({
      ...prev,
      main: value / 100
    }));
  };

  const handleVolumePreset = (value) => {
    setVolumeSettings(prev => ({
      ...prev,
      main: value / 100
    }));
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'classical' && !showAllPresets) {
      setShowAllPresets(true);
    }
  };

  // Quick tempo adjustment buttons for mobile
  const quickAdjustments = [
    { label: '-10', value: -10 },
    { label: '-5', value: -5 },
    { label: '-1', value: -1 },
    { label: '+1', value: 1 },
    { label: '+5', value: 5 },
    { label: '+10', value: 10 },
  ];

  return (
    <div className="flex flex-col">
      {/* Mobile-optimized Tempo Display */}
      <motion.div 
        className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 sm:p-6 text-center mb-4"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-5xl sm:text-6xl md:text-4xl font-bold font-mono text-primary-500 dark:text-primary-400 mb-2">
          {tempo}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('bpm')}
        </div>
        
        {/* Mobile: Large quick adjustment buttons */}
        <div className="block sm:hidden mb-4">
          <div className="grid grid-cols-3 gap-2 mb-3">
            {quickAdjustments.slice(0, 3).map((adj) => (
              <motion.button
                key={adj.label}
                onClick={() => handleTempoChange(tempo + adj.value)}
                className="h-12 rounded-lg bg-white dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {adj.label}
              </motion.button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {quickAdjustments.slice(3).map((adj) => (
              <motion.button
                key={adj.label}
                onClick={() => handleTempoChange(tempo + adj.value)}
                className="h-12 rounded-lg bg-white dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {adj.label}
              </motion.button>
            ))}
          </div>
        </div>
        
        {/* Desktop/Tablet: Original slider controls */}
        <div className="hidden sm:flex items-center gap-2">
          <motion.button 
            onClick={() => handleTempoChange(tempo - 1)}
            className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiMinus} className="h-4 w-4" />
          </motion.button>
          
          <input 
            type="range" 
            min="30" 
            max="240" 
            value={tempo}
            onChange={(e) => handleTempoChange(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
          />
          
          <motion.button 
            onClick={() => handleTempoChange(tempo + 1)}
            className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} className="h-4 w-4" />
          </motion.button>
        </div>

        {/* Mobile: Large slider for fine control */}
        <div className="block sm:hidden mt-4">
          <input 
            type="range" 
            min="30" 
            max="240" 
            value={tempo}
            onChange={(e) => handleTempoChange(parseInt(e.target.value))}
            className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${((tempo - 30) / (240 - 30)) * 100}%, #e5e7eb ${((tempo - 30) / (240 - 30)) * 100}%, #e5e7eb 100%)`
            }}
          />
        </div>
      </motion.div>
      
      {/* Mobile-optimized Tempo Presets */}
      <div className="mb-4">
        {/* Category Tabs */}
        <div className="flex mb-3">
          <button
            onClick={() => handleCategoryChange('basic')}
            className={`flex-1 py-3 text-sm font-medium rounded-l-lg border border-r-0 transition-colors ${
              activeCategory === 'basic' 
                ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300' 
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {t('tempoPresets.basic')}
          </button>
          <button
            onClick={() => handleCategoryChange('classical')}
            className={`flex-1 py-3 text-sm font-medium rounded-r-lg border transition-colors ${
              activeCategory === 'classical' 
                ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300' 
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {t('tempoPresets.classical')}
          </button>
        </div>
        
        {/* Basic Tempo Presets - Mobile optimized */}
        {activeCategory === 'basic' && (
          <div className="mb-2">
            <div className="grid grid-cols-2 gap-3">
              {basicTempoPresets.map((preset, index) => (
                <motion.button
                  key={preset.value}
                  onClick={() => handleTempoChange(preset.value)}
                  className={`px-4 py-4 sm:py-3 text-sm rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors min-h-[3.5rem] sm:min-h-[auto] ${
                    tempo === preset.value ? 'ring-2 ring-primary-300 dark:ring-primary-500' : ''
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="block font-medium">{preset.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {preset.value}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        )}
        
        {/* Classical Tempo Presets - Mobile optimized */}
        {activeCategory === 'classical' && (
          <div className="mt-1">
            <button 
              onClick={() => setShowAllPresets(!showAllPresets)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors mb-3"
            >
              <span className="text-sm font-medium">
                {showAllPresets ? t('tempoPresets.showLess') : t('tempoPresets.showMore')}
              </span>
              <SafeIcon 
                icon={showAllPresets ? FiChevronUp : FiChevronDown} 
                className="h-4 w-4" 
              />
            </button>
            
            <AnimatePresence>
              {showAllPresets && (
                <motion.div 
                  className="grid grid-cols-2 gap-3"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {classicalTempoPresets.map((preset, index) => (
                    <motion.button
                      key={preset.value}
                      onClick={() => handleTempoChange(preset.value)}
                      className={`px-4 py-4 sm:py-3 text-sm rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors min-h-[3.5rem] sm:min-h-[auto] ${
                        tempo === preset.value ? 'ring-2 ring-primary-300 dark:ring-primary-500' : ''
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="block font-medium truncate">{preset.label}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {preset.value}
                      </span>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Mobile-optimized Volume Control */}
      <motion.div 
        className="mb-4 block md:hidden"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('volumeControl')}
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('mainVolume')}
            </span>
            <span className="text-sm bg-gray-100 dark:bg-gray-600 rounded px-3 py-1 font-mono">
              {Math.round(volumeSettings.main * 100)}%
            </span>
          </div>
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={volumeSettings.main * 100}
            onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
            className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider mb-3"
            style={{
              background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${volumeSettings.main * 100}%, #e5e7eb ${volumeSettings.main * 100}%, #e5e7eb 100%)`
            }}
          />
          <div className="grid grid-cols-3 gap-2">
            <button 
              onClick={() => handleVolumePreset(0)}
              className="py-2 px-3 text-sm rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors font-medium"
            >
              {t('mute')}
            </button>
            <button 
              onClick={() => handleVolumePreset(50)}
              className="py-2 px-3 text-sm rounded bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors font-medium"
            >
              {t('medium')}
            </button>
            <button 
              onClick={() => handleVolumePreset(100)}
              className="py-2 px-3 text-sm rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium"
            >
              {t('max')}
            </button>
          </div>
        </div>
      </motion.div>
      
      {/* Mobile-optimized Play Controls */}
      <motion.div 
        className="flex justify-center gap-4 sm:gap-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <motion.div className="flex flex-col items-center">
          <motion.button
            onClick={togglePlay}
            className="w-20 h-20 sm:w-16 sm:h-16 md:w-16 md:h-16 rounded-full bg-primary-500 hover:bg-primary-600 text-white flex items-center justify-center shadow-lg relative overflow-hidden group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={isPlaying ? "Stop" : "Play"}
          >
            <div className="absolute inset-0 bg-white/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full"></div>
            <SafeIcon 
              icon={isPlaying ? FiPause : FiPlay} 
              className="h-10 w-10 sm:h-8 sm:w-8 md:h-8 md:w-8" 
            />
          </motion.button>
          <div className="text-xs mt-2 text-gray-600 dark:text-gray-400 text-center max-w-[120px]">
            {t('playInstruction')}
          </div>
        </motion.div>
        
        <motion.div className="flex flex-col items-center">
          <motion.button
            onClick={handleTapTempo}
            className="w-20 h-20 sm:w-16 sm:h-16 md:w-16 md:h-16 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 flex flex-col items-center justify-center shadow-lg relative overflow-hidden group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={t('tapInstruction')}
          >
            <div className="absolute inset-0 bg-primary-400/20 dark:bg-primary-500/20 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full"></div>
            <SafeIcon icon={MdTouchApp} className="h-8 w-8 sm:h-7 sm:w-7 md:h-7 md:w-7 mb-1" />
            <span className="text-xs font-medium">
              {t('tap')}
            </span>
          </motion.button>
          <div className="text-xs mt-2 text-gray-600 dark:text-gray-400 text-center max-w-[120px]">
            {t('tapInstruction')}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TempoControl;